import React from 'react';
import { getPbBarStyle } from '../../utils/chartUtils';

const normCompany = (name) => String(name || '').trim().toLowerCase();

const isSubjectCompany = (label, subjectCompany) => {
  const subjectKey = normCompany(subjectCompany);
  return Boolean(subjectKey && normCompany(label) === subjectKey);
};

const CompetitorVolumeChart = ({ data, subjectCompany, setTooltip }) => {
  if (!data || !data.labels || !data.datasets) return null;

  const totals = data.labels.map((_, i) =>
    data.datasets.reduce((sum, dataset) => sum + (dataset.data[i] || 0), 0)
  );

  const hexToHsl = (hex) => {
    if (!hex || hex.length < 7) return { h:0, s:0, l:100 };
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = s = 0; } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: h = 0; break;
      }
      h /= 6;
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  };

  const maxTotal = Math.max(...totals, 1);
  const yMax = Math.ceil(maxTotal / 10) * 10;
  const yAxisTicks = [yMax, yMax * 0.75, yMax * 0.5, yMax * 0.25, 0];

  const tickTopPct = (index) => (index / (yAxisTicks.length - 1)) * 100;

  return (
    <div className="volume-chart-container">
      <div className="volume-chart-main">
        {/* Y Axis */}
        <div className="volume-chart-y-axis">
          {yAxisTicks.map((tick, i) => (
            <div
              key={i}
              className="chart-axis-tick-value volume-chart-y-tick"
              style={{ top: `${tickTopPct(i)}%` }}
            >
              {tick}
            </div>
          ))}
        </div>

        {/* Chart Grid & Bars Container */}
        <div className="volume-chart-grid-container">
          {/* Grid lines */}
          <div className="volume-chart-grid-lines">
            {yAxisTicks.map((_, i) => (
              <div
                key={i}
                className="volume-chart-grid-line"
                style={{
                  top: `${tickTopPct(i)}%`,
                  backgroundColor: i === yAxisTicks.length - 1 ? 'transparent' : 'var(--border)',
                }}
              />
            ))}
          </div>

          {/* Bars wrapper */}
          <div className="volume-chart-bars-wrapper">
            {data.labels.map((label, idx) => {
              const total = totals[idx];
              const heightPct = (total / yMax) * 100;
              const isSubject = isSubjectCompany(label, subjectCompany);

              return (
                <div key={idx} className="volume-chart-bar-column" style={{ '--chart-bar-delay': `${0.2 + idx * 0.05}s` }}>
                  {/* The stacked bar */}
                  <div className="volume-chart-stacked-bar" style={{ height: `${heightPct}%` }}>
                    {data.datasets.map((ds, dsIdx) => {
                      const val = ds.data[idx];
                      if (!val) return null;
                      const segmentHeightPct = (val / total) * 100;
                      const hsl = hexToHsl(ds.backgroundColor || '#ffffff');
                      const textColor = hsl.l < 62 ? '#FFFFFF' : 'var(--c-black)';
                      
                      return (
                        <div 
                          key={dsIdx} 
                          className="volume-chart-bar-segment" 
                          style={{
                            height: `${segmentHeightPct}%`,
                            ...getPbBarStyle(ds.backgroundColor),
                            borderRadius: 0, 
                            color: textColor,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            if (!setTooltip) return;
                            setTooltip({
                              x: e.clientX,
                              y: e.clientY,
                              content: (
                                <>
                                  <div style={{ fontWeight: 700, marginBottom: '2px' }}>{label}</div>
                                  <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{ds.label}</div>
                                  <div style={{ marginTop: '4px', fontWeight: 600 }}>{val} filings</div>
                                </>
                              )
                            });
                          }}
                          onMouseMove={(e) => {
                            if (!setTooltip) return;
                            setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
                          }}
                          onMouseLeave={() => setTooltip && setTooltip(null)}
                        >
                          {val > 2 ? val : ''}
                        </div>
                      );
                    })}
                  </div>

                  {/* Rotated Labels below the bar */}
                  <div
                    title={label}
                    className="volume-chart-label"
                    onMouseEnter={(e) => {
                      if (!setTooltip) return;
                      setTooltip({
                        x: e.clientX,
                        y: e.clientY,
                        content: (
                          <>
                            <div style={{ fontWeight: 700, marginBottom: '2px' }}>{label}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{isSubject ? 'Our company' : 'Aggregated metric'}</div>
                            <div style={{ marginTop: '4px', fontWeight: 600 }}>{total} Total Filings</div>
                          </>
                        )
                      });
                    }}
                    onMouseMove={(e) => {
                      if (!setTooltip) return;
                      setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
                    }}
                    onMouseLeave={() => setTooltip && setTooltip(null)}
                  >
                    <span className="volume-chart-label-content">
                      <span className="volume-chart-company-dot-slot" aria-hidden="true">
                        {isSubject ? <span className="volume-chart-company-dot" /> : null}
                      </span>
                      <span className="chart-axis-label-text">{label}</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorVolumeChart;
